/**
 * LMA Price Updater
 * Dynamically updates product price based on selected quantity
 */

(function() {
  'use strict';

  const initializedContainers = new WeakSet();
  let productVariants = {};
  let currentVariantId = null;

  /**
   * Get product data from available sources
   */
  function getProductData() {
    if (window.product) return window.product;
    
    if (window.ShopifyAnalytics?.meta?.product) {
      return window.ShopifyAnalytics.meta.product;
    }
    
    const productScript = document.querySelector('script[type="application/json"][data-product-json]');
    if (productScript) {
      try {
        return JSON.parse(productScript.textContent);
      } catch (e) {
        return null;
      }
    }
    
    return null;
  }

  /**
   * Load all variant pricing data
   */
  function loadVariantData() {
    const productData = getProductData();
    
    if (!productData?.variants) return false;
    
    productData.variants.forEach(variant => {
      productVariants[variant.id] = {
        price: variant.price,
        compare_at_price: variant.compare_at_price
      };
    });
    
    // Get current variant ID
    const variantIdInput = document.querySelector('input[name="id"], select[name="id"]');
    if (variantIdInput) {
      currentVariantId = parseInt(variantIdInput.value);
    } else if (productData.selected_or_first_available_variant) {
      currentVariantId = productData.selected_or_first_available_variant.id;
    } else if (productData.variants.length > 0) {
      currentVariantId = productData.variants[0].id;
    }
    
    return true;
  }

  /**
   * Format price from cents to display format
   */
  function formatPrice(priceInCents) {
    if (typeof priceInCents !== 'number') return '';
    return '$' + (priceInCents / 100).toFixed(2);
  }

  /**
   * Update price display element
   */
  function updatePriceElement(element, priceInCents, quantity = 1) {
    if (!element || typeof priceInCents !== 'number') return;
    
    const totalPrice = priceInCents * quantity;
    const formattedPrice = formatPrice(totalPrice);
    
    const ymqSpan = element.querySelector('.ymq-price-span');
    if (ymqSpan) {
      ymqSpan.textContent = formattedPrice;
    } else {
      element.textContent = formattedPrice;
    }
  }

  /**
   * Find quantity controls with simplified fallback
   */
  function findQuantityControls(container) {
    // Try container first
    let quantityInput = container.querySelector('input[data-quantity-field]');
    let plusButton = container.querySelector('[data-quantity-plus]');
    let minusButton = container.querySelector('[data-quantity-minus]');
    
    // Global fallback if not in container
    if (!quantityInput) {
      quantityInput = document.querySelector('input[data-quantity-field]') || 
                     document.querySelector('input[name="quantity"]');
      plusButton = document.querySelector('[data-quantity-plus]');
      minusButton = document.querySelector('[data-quantity-minus]');
    }
    
    return { quantityInput, plusButton, minusButton };
  }

  /**
   * Initialize price updater for a container
   */
  function initializePriceUpdater(container) {
    if (initializedContainers.has(container)) return;
    initializedContainers.add(container);

    // Find price elements
    const priceWrapper = container.querySelector('[data-price-wrapper]');
    const priceDisplay = priceWrapper ? 
      priceWrapper.querySelector('[data-product-price]') : 
      container.querySelector('[data-product-price]');
    const comparePriceDisplay = container.querySelector('[data-compare-price]');
    
    // Find quantity controls
    const { quantityInput, plusButton, minusButton } = findQuantityControls(container);

    if (!quantityInput || !priceDisplay) return;

    /**
     * Update prices based on current quantity and variant
     */
    function updatePrices() {
      const quantity = parseInt(quantityInput.value) || 1;
      const variant = productVariants[currentVariantId];
      
      if (!variant?.price) return;
      
      updatePriceElement(priceDisplay, variant.price, quantity);
      
      if (comparePriceDisplay && variant.compare_at_price) {
        updatePriceElement(comparePriceDisplay, variant.compare_at_price, quantity);
      }
    }

    /**
     * Handle variant changes
     */
    function handleVariantChange() {
      const variantIdInput = document.querySelector('input[name="id"], select[name="id"]');
      if (variantIdInput) {
        const newVariantId = parseInt(variantIdInput.value);
        if (newVariantId && newVariantId !== currentVariantId) {
          currentVariantId = newVariantId;
          updatePrices();
        }
      }
    }

    // Quantity change listeners
    if (plusButton) {
      plusButton.addEventListener('click', () => setTimeout(updatePrices, 10));
    }

    if (minusButton) {
      minusButton.addEventListener('click', () => setTimeout(updatePrices, 10));
    }

    quantityInput.addEventListener('input', updatePrices);
    quantityInput.addEventListener('change', updatePrices);

    // Variant change listeners (consolidated)
    document.addEventListener('variant:change', handleVariantChange);
    document.addEventListener('change', (event) => {
      if (event.target.name === 'id' || 
          (event.target.name && event.target.name.includes('option'))) {
        setTimeout(handleVariantChange, 50);
      }
    });

    // Simple mutation observer to catch price resets
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => {
        const quantity = parseInt(quantityInput.value) || 1;
        if (quantity > 1) {
          setTimeout(updatePrices, 50);
        }
      });
      
      observer.observe(priceDisplay, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // Initial update
    updatePrices();
  }

  /**
   * Initialize all price updaters
   */
  function initializeAll() {
    if (!loadVariantData()) return;
    
    const containers = document.querySelectorAll('#lma__submit__holder');
    containers.forEach(container => {
      try {
        initializePriceUpdater(container);
      } catch (error) {
        // Silent fail for individual containers
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll);
  } else {
    initializeAll();
  }

  // Single retry for delayed content
  setTimeout(initializeAll, 500);

})(); 